/*!
 * @license
 * Alfresco Example Content Application
 *
 * Copyright (C) 2005 - 2020 Alfresco Software Limited
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail.  Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with Alfresco. If not, see <http://www.gnu.org/licenses/>.
 */

import { RuleContext } from '@alfresco/adf-extensions';
// import * as navigation from '@alfresco/aca-shared/rules';
// import { PathElement } from '@alfresco/js-api';

/**
 * Checks if user selected anything.
 * JSON ref: `app.selection.notEmpty`
 */
export function hasSelection(context: RuleContext): boolean {
  return !context.selection.isEmpty;
}
export function hasFolderSelected(context: RuleContext): boolean {
  if (context && context.selection && context.selection.folder) {
    return true;
  }
  return false;
}
export function hasFileSelected(context: RuleContext): boolean {
  if (context && context.selection && context.selection.file) {
    return true;
  }
  return false;
}


/**
 * Checks if node is cl:categoryFolder.
 * JSON ref: `jobdocuments.navigation.isCategoryFolder`
 */
export function isCategoryFolder(context: RuleContext): boolean {
  //console.log("isCategoryFolder folder ", context);
  if (context && context.navigation && context.navigation.currentFolder
    && context.navigation.currentFolder.nodeType == 'ch:categoryFolder' ) {

      //console.log("isCategoryFolder folder true", context);
      return true;
  }

  return false;
}

/**
 * Checks if node is in the business function Supporting Documents.
 * JSON ref: `jobdocuments.navigation.isSupportingDocumentsFolder`
 */
 export function isSupportingDocumentsFolder(context: RuleContext): boolean {
  //console.log("isSupportingDocumentsFolder folder ", context);
  if (context && context.navigation && context.navigation.currentFolder
    && context.navigation.currentFolder.nodeType == 'ch:businessFunctionFolder'
    && context.navigation.currentFolder.name === "Supporting Documents" ) {

      //console.log("isSupportingDocumentsFolder folder true", context);
      return true;
  }

  return false;
}

/**
 * Checks if node is ch:jobDocument.
 * JSON ref: `jobdocuments.selection.isJobDocument`
 */
 export function isJobDocument(context: RuleContext): boolean {
  //console.log('Rule Context: ', context);
  if (hasFileSelected(context)) {
    if (context?.selection?.file?.entry.nodeType === 'ch:jobDocument') {
      //console.log('isJobDocument ', true);
      return true;
    }
  }

  return false;
}

/**
 * Checks if user has Role of IT Admin.
 * JSON ref: `user.isITAdmin`
 */
export function isITAdmin(context: RuleContext): boolean {
  let groups = context.profile.groups;
  if (groups && groups.length > 0) {
    for (var group of groups) {
      if (group.id == 'GROUP_ORG-CH-IT-OPS-APP SUPPORT' || group.id == 'GROUP_ORG-CH-IT-OPS-INFRASTRUCTURE'  ) {
        return true;
      }
    }
  }
  return false;
}
export function isAlfrescoAdmin(context: RuleContext): boolean {
  let isAdmin = context.profile.isAdmin;
  if (isAdmin) {
    return true;
  }
  return false;
}

export function hasAdminRole(context: RuleContext): boolean {
  //console.log("hasAdminRole rule called", context);
  if (isAlfrescoAdmin(context) || isITAdmin(context)) {
    return true;
  }
  return false;
}

export function hasDisplayTeamRole(context: RuleContext): boolean {
  let groups = context.profile.groups;
  if (groups && groups.length > 0) {
    for (var group of groups) {
      if (group.id == 'GROUP_DISPLAY_PROJECT_USERS' ) {
        return true;
      }
    }
  }
  return false;
}



/**
 * Checks if user has Bluebeam edit role.
 * JSON ref: `jobdocuments.role.hasBluebeamEditRole`
 */
export function hasBluebeamEditRole(context: RuleContext): boolean {
  console.log("hasBluebeamEditRole rule called", context);
  let groups = context.profile.groups;
  if (groups && groups.length > 0) {
    for (var group of groups) {
      if (group.id == 'GROUP_APP-USER-INT-ALFRESCO-BLUEBEAM' ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Checks if the file can be opened with bluebeam
 * JSON ref: `jobdocuments.selection.canOpenWithBluebeam`
 *
 * @param context Rule execution context
 */
 export function canOpenWithBluebeam(context: RuleContext): boolean {

  console.log("canOpenWithBluebeam rule called", context);

  if (context.navigation?.url?.startsWith('/trashcan')) {
    return false;
  }

  if (!context?.selection) {
    console.log("canOpenWithBluebeam rule: selection false");
    return false;
  }

  const { file } = context.selection;

  if (!file?.entry) {
    console.log("canOpenWithBluebeam rule: file.entry false");
    return false;
  }

  const extension = getFileExtension(file.entry.name);
  if (!extension || extension != 'pdf') {
    console.log("canOpenWithBluebeam rule: file extension");
    return false;
  }

  if (!file.entry.properties) {
    console.log("canOpenWithBluebeam rule: file properties false");
    return false;
  }

  if (file.entry.isLocked) {
    console.log("canOpenWithBluebeam rule: isLocked false");
    return false;
  }

  if (file.entry.properties['cm:lockType'] === 'WRITE_LOCK' || file.entry.properties['cm:lockType'] === 'READ_ONLY_LOCK') {
    console.log("canOpenWithBluebeam rule: locktype false");
    return false;
  }

  const lockOwner = file.entry.properties['cm:lockOwner'];
  if (lockOwner && lockOwner.id !== context.profile.id) {
    console.log("canOpenWithBluebeam rule: lockowner false");
    return false;
  }

  // workaround for Shared files
  if (context.navigation?.url?.startsWith('/shared')) {
    // eslint-disable-next-line no-prototype-builtins
    if (file.entry.hasOwnProperty('allowableOperationsOnTarget')) {
      console.log("canOpenWithBluebeam rule: permissionCheck", context.permissions.check(file, ['update'], {
        target: 'allowableOperationsOnTarget'
      }));
      return context.permissions.check(file, ['update'], {
        target: 'allowableOperationsOnTarget'
      });
    }
  }

  console.log("canOpenWithBluebeam rule: last permission check", context.permissions.check(file, ['update']));

  return context.permissions.check(file, ['update']);
}


export function getFileExtension(fileName: string): string | null {
  if (fileName) {
    const match = fileName.toLowerCase().match(/\.([^\./\?\#]+)($|\?|\#)/);

    return match ? match[1] : null;
  }

  return null;
}

